// src/components/GooglePlacesInput.jsx
// Reusable Google Places Autocomplete input.
// Emits onPlace({ placeId, name, address, lat, lng }) on selection, or null on clear.

import { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Singleton loader — only initialises the Maps SDK once across the app
let _loader = null;
const getLoader = () => {
  if (!_loader) {
    _loader = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
      version: 'weekly',
      libraries: ['places'],
    });
  }
  return _loader;
};

export default function GooglePlacesInput({
  onPlace,
  defaultValue = '',
  placeholder = 'Search for a location…',
  className = '',
}) {
  const inputRef          = useRef(null);
  const autocompleteRef   = useRef(null);
  const [value, setValue] = useState(defaultValue);
  const [ready, setReady] = useState(false);

  // Load the SDK once
  useEffect(() => {
    getLoader().load().then(() => setReady(true)).catch(console.error);
  }, []);

  // Attach Autocomplete once SDK is ready
  useEffect(() => {
    if (!ready || !inputRef.current) return;
    const google = window.google;

    autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['establishment', 'geocode'],
      fields: ['place_id', 'geometry', 'name', 'formatted_address'],
    });

    const listener = autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (!place?.geometry) return;

      const result = {
        placeId:  place.place_id          || null,
        name:     place.name              || '',
        address:  place.formatted_address || '',
        lat:      place.geometry.location.lat(),
        lng:      place.geometry.location.lng(),
      };

      setValue(result.address || result.name);
      onPlace?.(result);
    });

    return () => google.maps.event.removeListener(listener);
  }, [ready, onPlace]);

  const clear = () => {
    setValue('');
    onPlace?.(null);
    if (inputRef.current) inputRef.current.value = '';
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <MapPinIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className="block w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 pl-9 pr-8 py-2 text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
      />
      {value && (
        <button
          type="button"
          onClick={clear}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
          aria-label="Clear location"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
